<template>
  <div class="previred-facial-recognition">
    <div class="sw-step">
      <h3 class="title-get-code">Valida tu identidad</h3>
      <div class="second-factor-info" v-if="showInfo">
        <p class="text-get-code">
          Por tu seguridad, necesitamos confirmar tu identidad mediante reconocimiento facial y un
          código de verificación antes de continuar.
        </p>
        <div class="factor-detail">
          <div>
            <span class="factor-detail-pretitle">Método 1</span>
            <h3 class="factor-detail-title">
              <img src="../assets/scan-face 1.png" alt="icon scan face" />
              Reconocimiento Facial
            </h3>
            <p class="factor-detail-text">
              Ten a mano tu cédula de identidad y tu teléfono celular
            </p>
          </div>
          <img class="fill-icon" src="../assets/Fill.png" alt="fill icon" />
        </div>
        <div class="factor-detail">
          <div>
            <span class="factor-detail-pretitle">Método 2</span>
            <h3 class="factor-detail-title">
              <img src="../assets/key 1.png" alt="icon key" />
              Código de verificación
            </h3>
            <p class="factor-detail-text">
              Se enviará un código de 6 dígitos a tu correo electrónico registrado en Cuprum AFP.
            </p>
          </div>
          <img class="fill-icon" src="../assets/Fill.png" alt="fill icon" />
        </div>
        <button class="fulfillment-button" id="accept-auth-method" @click="createAuthMethod">
          Comenzar validación
        </button>
      </div>
      <div class="auth" v-else-if="showAuthWidget">
        <FulfillmentAuthentication
          :rut="rut"
          :company="'cuprum'"
          :method="'facialRecognition'"
          :dummy-mode="dummyMode"
          :kong-token="kongToken"
          :kong-client-id="kongClientId"
          :kong-client-secret="kongClientSecret"
          :environment="environment"
          :facial-timer="facialTimer"
          :channel-key="channelKey"
          @auth="onAuthentication"
          @goBack="$emit('goBack')"
        ></FulfillmentAuthentication>
      </div>
      <div class="previred" v-else>
        <Result
          v-if="isPollingOn && !hasErrors"
          title="Tu identidad ha sido validada"
          body="Por favor espera, estamos obteniendo tus datos de afiliado"
        >
          <img
            class="hourglass"
            slot="image"
            src="https://static.dx.cuprum.cl/fulfillment/assets/previred/hourglass.png"
            alt="waiting"
          />
        </Result>
        <Result
          v-if="!isPollingOn && !hasErrors"
          title="Tus datos han sido obtenidos"
          body="Ya puedes migrar a CuprumAFP"
        >
          <img
            slot="image"
            src="https://static.dx.cuprum.cl/fulfillment/assets/auth/kba/success_image.svg"
            alt="success_image"
          />
        </Result>
        <Result
          v-if="hasErrors"
          title="No es posible verificar tu información"
          :body="errorDetail || 'Por la seguridad de tu información no es posible continuar.'"
        >
          <img
            slot="image"
            src="https://static.dx.cuprum.cl/fulfillment/assets/auth/kba/error_image.svg"
            alt="error_image"
          />
        </Result>
      </div>
    </div>
  </div>
</template>

<script>
import { FFResultEvent } from "@/services/previred/structure-response";
import {
  VALIDATION_TYPE,
  isValidationError,
  validateClientValidationId,
} from "@/services/previred/validation-id";
import FulfillmentAuthentication from "fulfillment-cuprum-auth-widget";
import Result from "./ResultComponent";

const RESULT_EVENT = "previredFacialRecognition";

export default {
  name: "PreviredFacialRecognition",
  components: { FulfillmentAuthentication, Result },
  props: {
    rut: { type: String, required: true },
    dummyMode: { type: Boolean, default: false },
    kongToken: { type: String },
    kongClientId: { type: String },
    kongClientSecret: { type: String },
    previredAPI: { type: Object, required: true },
    previredService: { type: Object, required: true },
    environment: { type: String },
    facialTimer: { type: Number, default: 3 },
    channelKey: { type: String },
  },
  data() {
    return {
      showInfo: true,
      showAuthWidget: false,
      isPollingOn: false,
      hasErrors: false,
      errorDetail: "",
    };
  },
  methods: {
    createAuthMethod() {
      this.showInfo = false;
      this.showAuthWidget = true;
    },
    failWithValidationId(validationError) {
      this.hasErrors = true;
      this.errorDetail = validationError.userMessage;
      const error = {
        reason: validationError.code,
        message: validationError.message,
        source: "PreviredWidget",
      };
      this.$emit(RESULT_EVENT, FFResultEvent.failure(error));
    },
    onAuthentication(authResponse) {
      this.showInfo = false;
      this.showAuthWidget = false;
      this.$emit("auth", authResponse);
      if (this.dummyMode) {
        const successResponse = {
          transactionId: "transaction-12345",
          clientIP: "127.0.0.1",
          executiveIP: "127.0.0.1",
          clientProvider: "dummy-provider",
        };
        this.$emit(RESULT_EVENT, FFResultEvent.success(successResponse));
      } else {
        if (authResponse.result) {
          if (authResponse.result.status === "APPROVED" && authResponse.result.certificate) {
            const idCheck = validateClientValidationId(
              authResponse.result.transactionId,
              VALIDATION_TYPE.FACIAL_RECOGNITION
            );
            if (!idCheck.valid) {
              this.failWithValidationId(idCheck.error);
              return;
            }
            this.previredService
              .selfValidation({
                certificate: authResponse.result.certificate,
                clientValidationId: idCheck.value,
              })
              .then((response) => {
                if (response.status === "CONFIRMED") {
                  this.pollToken(response.transactionId, Date.now());
                } else {
                  this.hasErrors = true;
                  const error = {
                    reason: "INVALID_TRANSACTION_STATUS",
                    message: `Status: ${response.status}`,
                    source: "PreviredWidget",
                  };
                  this.$emit(RESULT_EVENT, FFResultEvent.failure(error));
                }
              })
              .catch((err) => {
                if (isValidationError(err)) {
                  this.failWithValidationId(err);
                  return;
                }
                this.hasErrors = true;
                const error = {
                  reason: "FAILURE_CREATING_TRANSACTION",
                  message: err.message,
                  source: "PreviredWidget",
                };
                this.$emit(RESULT_EVENT, FFResultEvent.failure(error));
              });
          } else {
            this.hasErrors = true;
            let errorResponse = {
              reason: "AUTH_VALIDATION_FAILED",
              message: "Status response : " + authResponse.result.status,
            };
            this.$emit(RESULT_EVENT, FFResultEvent.failure(errorResponse));
          }
        } else {
          this.hasErrors = true;
          let errorResponse = authResponse;
          if (errorResponse.error) {
            if (errorResponse.error.source) {
              errorResponse.source = "AuthWidget";
            }
          } else {
            errorResponse = FFResultEvent.failure(
              {
                reason: "AUTH_VALIDATION_ERROR",
                message: "UNEXPECTED ERROR",
                source: "AuthWidget",
              }
            );
          }
          this.$emit(RESULT_EVENT, errorResponse);
        }
      }
    },
    pollToken(transactionId, startTime) {
      this.isPollingOn = true;
      let timePassed = Date.now() - startTime;
      if (timePassed < process.env.VUE_APP_PREVIRED_POLLING_MAX_TIME) {
        setTimeout(() => {
          this.previredService
            .getToken(transactionId)
            .then((response) => {
              this.tokenStatus = response.status;
              if (this.tokenStatus === "UNCONFIRMED" || this.tokenStatus === "PENDING") {
                this.pollToken(transactionId, startTime);
              } else if (this.tokenStatus === "OK") {
                this.isPollingOn = false;
                const successResponse = {
                  transactionId: transactionId,
                  clientIP: response.clientIP,
                  executiveIP: response.executiveIP,
                  clientProvider: response.clientProvider,
                };
                this.$emit(RESULT_EVENT, FFResultEvent.success(successResponse));
              } else {
                this.hasErrors = true;
                this.isPollingOn = false;
                const errorResponse = {
                  reason:
                    this.tokenStatus === "EXPIRED" ? "TRANSACTION_EXPIRED" : "TRANSACTION_ERROR",
                  message: "Status in token response : " + response.status,
                };
                this.$emit(RESULT_EVENT, FFResultEvent.failure(errorResponse));
              }
            })
            .catch((error) => {
              this.pollingErrorRetries -= 1;
              if (this.pollingErrorRetries > 0) {
                this.pollToken(transactionId, startTime);
              } else {
                this.hasErrors = true;
                this.isPollingOn = false;
                const errorResponse = {
                  reason: "PREVIRED_API_ERROR",
                  message: error.message,
                  source: "PreviredAPI",
                };
                this.$emit(RESULT_EVENT, FFResultEvent.failure(errorResponse));
              }
            });
        }, this.pollingIntervalInMillis);
      } else {
        this.hasErrors = true;
        this.isPollingOn = false;
        const error = {
          reason: "POLLING_TIMEOUT",
          message: "Polling exceeds the maximum time allowed",
        };
        this.$emit(RESULT_EVENT, FFResultEvent.failure(error));
      }
    },
  },
};
</script>

<style scoped>
.sw-step {
  align-items: center;
  height: 100%;
  width: 100%;
  color: #484848;
  font-size: 0.9rem;
}

.errors {
  color: red;
}

img.hourglass {
  /* Start the shake animation and make the animation last for 0.5 seconds */
  animation: shake 2s;
  /* When the animation is finished, start again */
  animation-iteration-count: infinite;
}

img.success {
  animation: expand 2s;
  animation-iteration-count: 1;
}

.title-get-code {
  font-family: FS Elliot Pro, Helvetica, Arial, sans-serif;
  font-size: 24px;
  letter-spacing: 0;
  line-height: 35px;
  text-align: left;
  padding: 0 16px;
}

.text-get-code {
  color: #464646;
  font-family: FS Elliot Pro, Helvetica, Arial, sans-serif;
  font-size: 16px;
  letter-spacing: 0;
  line-height: 24px;
  text-align: justify;
  width: 100%;
}

.second-factor-info {
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 8px;
}

.second-factor-info > * {
  margin: 0;
  padding: 0;
}

.fulfillment-button {
  height: 50px;
  border-radius: 25px;
  color: #ffffff;
  font-size: 17px;
  font-weight: bold;
  letter-spacing: 0;
  text-align: center;
  border: 0;
  margin-top: 23px;
  align-self: center;
  width: auto;
  padding: 10px 32px;
  line-height: 0;
  background-color: #0061a0;
}

.factor-detail {
  border: 1px solid #cecece;
  border-radius: 8px;
  padding: 8px 16px;
  color: #464646;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 8px;
}

.factor-detail > .fill-icon {
  align-self: center;
}

.factor-detail-title {
  margin: 4px 0;
}

.factor-detail-title > img {
  margin-right: 4px;
  vertical-align: bottom;
}

.factor-detail-text {
  margin: 0;
}

@keyframes shake {
  0% {
    transform: rotate(0deg);
  }

  10% {
    transform: rotate(-10deg);
  }

  20% {
    transform: rotate(0deg);
  }

  30% {
    transform: rotate(10deg);
  }

  40% {
    transform: rotate(0deg);
  }

  50% {
    transform: rotate(-10deg);
  }

  60% {
    transform: rotate(0deg);
  }

  70% {
    transform: rotate(10deg);
  }

  80% {
    transform: rotate(0deg);
  }

  90% {
    transform: rotate(-10deg);
  }

  100% {
    transform: rotate(0deg);
  }
}

@keyframes expand {
  0% {
    transform: width 110%, height 110%;
  }

  50% {
    transform: width 110%, height 110%;
  }

  100% {
    transform: width 110%, height 110%;
  }
}
</style>
